import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FiEdit2, FiPlus, FiSearch, FiTrash2, FiUserPlus, FiX } from "react-icons/fi";
import { usePermissions } from "../Auth/PermissionsContext";
import { AdminUser, AssignableRole, usersClient } from "./Users/usersClient";

/** Admin user management — create/edit/delete accounts and assign RBAC roles.
 * Backed by Auth0 in production (a password is required on create) or the local DB in dev. */
export default function Users(): React.ReactElement {
	const { can } = usePermissions();
	const canWrite = can("feature:users:write");

	const [users, setUsers] = useState<AdminUser[]>([]);
	const [roles, setRoles] = useState<AssignableRole[]>([]);
	const [source, setSource] = useState<string>("local");
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState("");
	const [editing, setEditing] = useState<AdminUser | null>(null);
	const [creating, setCreating] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const [list, assignable] = await Promise.all([usersClient.list(search.trim() || undefined), usersClient.assignableRoles()]);
			setUsers(list.users);
			setSource(list.source);
			setRoles(assignable);
		} catch (e: any) {
			toast.error(e?.response?.data?.message ?? "Impossible de charger les utilisateurs.");
		} finally {
			setLoading(false);
		}
	}, [search]);

	useEffect(() => {
		void load();
	}, [load]);

	const roleName = useMemo(() => {
		const m = new Map(roles.map((r) => [r.key, r.name]));
		return (key: string) => m.get(key) ?? key;
	}, [roles]);

	const onDelete = async (u: AdminUser) => {
		if (!window.confirm(`Supprimer l'utilisateur « ${u.email} » ?`)) return;
		try {
			await usersClient.remove(u.id);
			toast.success("Utilisateur supprimé.");
			void load();
		} catch (e: any) {
			toast.error(e?.response?.data?.message ?? "Suppression impossible.");
		}
	};

	return (
		<div className="mx-auto w-full max-w-6xl px-6 py-8">
			<div className="flex flex-wrap items-end justify-between gap-4">
				<div>
					<h1 className="text-2xl font-bold text-slate-900">Utilisateurs</h1>
					<p className="mt-1 text-sm text-slate-500">
						Gérez les comptes administrateurs et leurs rôles.{" "}
						<span className="font-medium text-slate-600">Source : {source === "auth0" ? "Auth0" : "Base locale"}</span>
					</p>
				</div>
				{canWrite && (
					<button
						type="button"
						onClick={() => setCreating(true)}
						className="inline-flex items-center gap-2 rounded-xl bg-[#0095DB] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#007cb8]"
					>
						<FiUserPlus className="h-4 w-4" /> Nouvel utilisateur
					</button>
				)}
			</div>

			<div className="mt-6 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
				<FiSearch className="h-4 w-4 text-slate-400" />
				<input
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					placeholder="Rechercher par e-mail ou nom…"
					className="w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
				/>
			</div>

			<div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
				<table className="w-full text-left text-sm">
					<thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
						<tr>
							<th className="px-5 py-3 font-semibold">Utilisateur</th>
							<th className="px-5 py-3 font-semibold">Rôles</th>
							<th className="px-5 py-3 font-semibold">Statut</th>
							{canWrite && <th className="px-5 py-3 text-right font-semibold">Actions</th>}
						</tr>
					</thead>
					<tbody className="divide-y divide-slate-100">
						{loading ? (
							<tr>
								<td colSpan={4} className="px-5 py-10 text-center text-slate-400">
									Chargement…
								</td>
							</tr>
						) : users.length === 0 ? (
							<tr>
								<td colSpan={4} className="px-5 py-10 text-center text-slate-400">
									Aucun utilisateur.
								</td>
							</tr>
						) : (
							users.map((u) => (
								<tr key={u.id} className="hover:bg-slate-50/60">
									<td className="px-5 py-3">
										<div className="flex items-center gap-3">
											<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-600">
												{initials(u.name || u.email)}
											</div>
											<div className="min-w-0">
												<p className="truncate font-medium text-slate-800">{u.name || u.email}</p>
												<p className="truncate text-xs text-slate-400">{u.email}</p>
											</div>
										</div>
									</td>
									<td className="px-5 py-3">
										<div className="flex flex-wrap gap-1.5">
											{u.roles.length === 0 ? (
												<span className="text-xs text-slate-400">—</span>
											) : (
												u.roles.map((r) => (
													<span key={r} className="rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-700">
														{roleName(r)}
													</span>
												))
											)}
										</div>
									</td>
									<td className="px-5 py-3">
										{u.blocked ? (
											<span className="rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-600">Bloqué</span>
										) : (
											<span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-600">Actif</span>
										)}
									</td>
									{canWrite && (
										<td className="px-5 py-3">
											<div className="flex justify-end gap-1">
												<button
													type="button"
													onClick={() => setEditing(u)}
													className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
													title="Modifier"
												>
													<FiEdit2 className="h-4 w-4" />
												</button>
												<button
													type="button"
													onClick={() => onDelete(u)}
													className="rounded-lg p-2 text-rose-500 transition hover:bg-rose-50"
													title="Supprimer"
												>
													<FiTrash2 className="h-4 w-4" />
												</button>
											</div>
										</td>
									)}
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			{(creating || editing) && canWrite && (
				<UserModal
					user={editing}
					roles={roles}
					requirePassword={source === "auth0"}
					onClose={() => {
						setCreating(false);
						setEditing(null);
					}}
					onSaved={() => {
						setCreating(false);
						setEditing(null);
						void load();
					}}
				/>
			)}
		</div>
	);
}

function UserModal({
	user,
	roles,
	requirePassword,
	onClose,
	onSaved
}: {
	user: AdminUser | null;
	roles: AssignableRole[];
	requirePassword: boolean;
	onClose: () => void;
	onSaved: () => void;
}): React.ReactElement {
	const isEdit = !!user;
	const [email, setEmail] = useState(user?.email ?? "");
	const [name, setName] = useState(user?.name ?? "");
	const [password, setPassword] = useState("");
	const [blocked, setBlocked] = useState(user?.blocked ?? false);
	const [selected, setSelected] = useState<Set<string>>(new Set(user?.roles ?? []));
	const [saving, setSaving] = useState(false);

	const toggleRole = (key: string) =>
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});

	const save = async () => {
		setSaving(true);
		try {
			if (isEdit) {
				await usersClient.update(user!.id, { name: name ?? "", blocked, roles: Array.from(selected) });
				toast.success("Utilisateur mis à jour.");
			} else {
				await usersClient.create({
					email,
					name: name || undefined,
					password: requirePassword ? password : undefined,
					roles: Array.from(selected)
				});
				toast.success("Utilisateur créé.");
			}
			onSaved();
		} catch (e: any) {
			toast.error(e?.response?.data?.message ?? "Enregistrement impossible.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/40 p-4">
			<div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
				<div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
					<h2 className="text-lg font-semibold text-slate-900">{isEdit ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</h2>
					<button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
						<FiX className="h-5 w-5" />
					</button>
				</div>
				<div className="max-h-[70vh] space-y-4 overflow-auto px-6 py-5">
					<Field label="Adresse e-mail">
						<input
							type="email"
							value={email}
							disabled={isEdit}
							onChange={(e) => setEmail(e.target.value)}
							className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400"
						/>
					</Field>
					<Field label="Nom">
						<input value={name ?? ""} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
					</Field>
					{!isEdit && requirePassword && (
						<Field label="Mot de passe initial">
							<input
								type="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
							/>
						</Field>
					)}
					{isEdit && (
						<label className="flex items-center gap-2 text-sm text-slate-700">
							<input type="checkbox" checked={blocked} onChange={(e) => setBlocked(e.target.checked)} /> Bloquer la connexion de cet utilisateur
						</label>
					)}
					<div>
						<p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Rôles</p>
						<div className="grid gap-2 sm:grid-cols-2">
							{roles.map((r) => (
								<label
									key={r.key}
									className={`flex cursor-pointer items-start gap-2 rounded-xl border p-3 text-sm transition ${
										selected.has(r.key) ? "border-sky-300 bg-sky-50" : "border-slate-200 hover:bg-slate-50"
									}`}
								>
									<input type="checkbox" className="mt-0.5" checked={selected.has(r.key)} onChange={() => toggleRole(r.key)} />
									<span>
										<span className="font-medium text-slate-800">{r.name}</span>
										{r.description && <span className="block text-xs text-slate-400">{r.description}</span>}
									</span>
								</label>
							))}
						</div>
					</div>
				</div>
				<div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
					<button type="button" onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">
						Annuler
					</button>
					<button
						type="button"
						onClick={save}
						disabled={saving || (!isEdit && (!email || (requirePassword && !password)))}
						className="inline-flex items-center gap-2 rounded-xl bg-[#0095DB] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#007cb8] disabled:opacity-50"
					>
						<FiPlus className="h-4 w-4" /> {isEdit ? "Enregistrer" : "Créer"}
					</button>
				</div>
			</div>
		</div>
	);
}

function Field({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
	return (
		<label className="block">
			<span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
			{children}
		</label>
	);
}

function initials(value: string): string {
	const parts = value.trim().split(/[\s@.]+/).filter(Boolean);
	return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}
